// components/CategoryFilter/CategoryFilter.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, Slide, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, TextField, InputAdornment, Chip, IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon     from '@mui/icons-material/Search';
import CloseIcon      from '@mui/icons-material/Close';
import { getRamos }     from '../../services/RamosServices';
import { getRubros }    from '../../services/RubrosServices';
import { getSubrubros } from '../../services/SubrubrosServices';
import { getMarcas }    from '../../services/MarcasServices';

// ─── Select con buscador interno ─────────────────────────────────────────────
const SearchableSelect = ({
  label, value, onChange, onClear,
  items, itemLabel, id, disabled,
}) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  const filtered = items?.filter(item =>
    item[itemLabel]?.toLowerCase().includes(search.toLowerCase())
  ) ?? [];

  const selectedLabel = items?.find(i => String(i.id) === String(value))?.[itemLabel];
  const hasValue      = !!value;

  const handleOpen  = () => { setSearch(''); setOpen(true); };
  const handleClose = () => { setSearch(''); setOpen(false); };
  const handleSelect = (val) => { onChange({ target: { value: val } }); handleClose(); };
  const handleClear  = (e) => { e.stopPropagation(); onClear(); };

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      {/* Campo visible — imita un Select pero con botón X ──────────────────── */}
      <Box
        onClick={disabled ? undefined : handleOpen}
        sx={{
          display: 'flex', alignItems: 'center',
          height: 40, px: 1.5,
          border: `1.5px solid ${hasValue ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-md)',
          bgcolor: disabled ? 'var(--bg-subtle)' : 'var(--bg-card)',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'border-color 0.2s ease',
          '&:hover': { borderColor: disabled ? 'var(--border)' : 'var(--accent)' },
          gap: 0.5,
        }}
      >
        <Typography sx={{
          flex: 1, fontSize: '0.82rem', fontFamily: 'var(--font-body)',
          color: hasValue ? 'var(--text-primary)' : 'var(--text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {hasValue ? selectedLabel : label}
        </Typography>

        {/* Chip del valor activo + botón X ────────────────────────────────── */}
        {hasValue && (
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              p: 0.25, color: 'var(--accent)',
              '&:hover': { bgcolor: 'var(--accent-soft)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}

        {/* Flecha dropdown */}
        <Box sx={{
          fontSize: '0.65rem', color: 'var(--text-muted)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          lineHeight: 1,
        }}>▼</Box>
      </Box>

      {/* Label flotante encima ───────────────────────────────────────────── */}
      <Typography sx={{
        position: 'absolute', top: -9, left: 10,
        fontSize: '0.7rem', fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--accent)' : 'var(--text-muted)',
        bgcolor: 'var(--bg-card)', px: 0.5, lineHeight: 1,
        pointerEvents: 'none',
      }}>
        {label}
      </Typography>

      {/* Dropdown ────────────────────────────────────────────────────────── */}
      {open && (
        <>
          {/* Overlay para cerrar */}
          <Box
            onClick={handleClose}
            sx={{ position: 'fixed', inset: 0, zIndex: 1200 }}
          />
          <Box sx={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300,
            bgcolor: 'var(--bg-card)', border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
            mt: 0.5, overflow: 'hidden',
          }}>
            {/* Buscador */}
            <Box sx={{ p: 1, borderBottom: '1px solid var(--border)' }}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder={`Buscar ${label.toLowerCase()}...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                inputRef={inputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem', fontFamily: 'var(--font-body)',
                    borderRadius: 'var(--radius-sm)',
                    '& fieldset': { borderColor: 'var(--border)' },
                    '&.Mui-focused fieldset': { borderColor: 'var(--accent)' },
                  },
                }}
              />
            </Box>

            {/* Lista de opciones */}
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {/* Opción Todos */}
              <Box
                onClick={() => handleSelect('')}
                sx={{
                  px: 2, py: 0.75, cursor: 'pointer',
                  fontSize: '0.82rem', fontFamily: 'var(--font-body)',
                  color: !value ? 'var(--accent)' : 'var(--text-muted)',
                  fontStyle: 'italic',
                  bgcolor: !value ? 'var(--accent-soft)' : 'transparent',
                  '&:hover': { bgcolor: 'var(--bg-subtle)' },
                }}
              >
                Todos
              </Box>

              {filtered.length > 0
                ? filtered.map(item => (
                    <Box
                      key={item.id}
                      onClick={() => handleSelect(String(item.id))}
                      sx={{
                        px: 2, py: 0.75, cursor: 'pointer',
                        fontSize: '0.82rem', fontFamily: 'var(--font-body)',
                        color: String(item.id) === String(value) ? 'var(--accent)' : 'var(--text-primary)',
                        bgcolor: String(item.id) === String(value) ? 'var(--accent-soft)' : 'transparent',
                        fontWeight: String(item.id) === String(value) ? 700 : 400,
                        transition: 'background 0.1s',
                        '&:hover': { bgcolor: 'var(--bg-subtle)' },
                      }}
                    >
                      {item[itemLabel]}
                    </Box>
                  ))
                : <Box sx={{ px: 2, py: 1.5, fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-body)' }}>
                    Sin resultados
                  </Box>
              }
            </Box>
          </Box>
        </>
      )}
    </FormControl>
  );
};

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// ─── CategoryFilter ───────────────────────────────────────────────────────────
export const CategoryFilter = ({
  ramoId,     setRamoId,
  rubroId,    setRubroId,
  subrubroId, setSubrubroId,
  marcaId,    setMarcaId,
  clearAll,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [modalOpen, setModalOpen] = useState(false);

  const [ramos,     setRamos]     = useState([]);
  const [rubros,    setRubros]    = useState([]);
  const [subrubros, setSubrubros] = useState([]);
  const [marcas,    setMarcas]    = useState([]);

  useEffect(() => {
    Promise.all([getRamos(), getRubros(), getSubrubros(), getMarcas()])
      .then(([r, ru, su, ma]) => {
        setRamos(r   || []);
        setRubros(ru || []);
        setSubrubros(su || []);
        setMarcas(ma || []);
      })
      .catch(console.error);
  }, []);

  const rubrosFiltered    = ramoId  ? rubros.filter(r => String(r.ramo_Id)  === String(ramoId))  : rubros;
  const subrubrosFiltered = rubroId ? subrubros.filter(s => String(s.rubro_Id) === String(rubroId)) : subrubros;

  // Contar filtros activos
  const activeCount = [ramoId, rubroId, subrubroId, marcaId].filter(Boolean).length;

  const FiltersContent = (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={3}>
        <SearchableSelect
          label="Ramo" value={ramoId}
          onChange={e => setRamoId(e.target.value)}
          onClear={() => setRamoId('')}
          items={ramos} itemLabel="descripcion_ramo" id="ramo-select"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <SearchableSelect
          label="Rubro" value={rubroId}
          onChange={e => setRubroId(e.target.value)}
          onClear={() => setRubroId('')}
          items={rubrosFiltered} itemLabel="descripcion_rubro" id="rubro-select"
          disabled={!ramoId}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <SearchableSelect
          label="Subrubro" value={subrubroId}
          onChange={e => setSubrubroId(e.target.value)}
          onClear={() => setSubrubroId('')}
          items={subrubrosFiltered} itemLabel="descripcion_subrubro" id="subrubro-select"
          disabled={!rubroId}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <SearchableSelect
          label="Marca" value={marcaId}
          onChange={e => setMarcaId(e.target.value)}
          onClear={() => setMarcaId('')}
          items={marcas} itemLabel="mar_descripcion" id="marca-select"
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      {isMobile ? (
        <>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Button
                variant="contained" fullWidth
                onClick={() => setModalOpen(true)}
                startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: 'var(--radius-md)', textTransform: 'none',
                  fontFamily: 'var(--font-body)', fontWeight: 600,
                  backgroundColor: activeCount > 0 ? 'var(--accent)' : 'var(--primary)',
                  '&:hover': { backgroundColor: 'var(--accent-hover)' },
                }}
              >
                Filtros222 {activeCount > 0 && (
                  <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, bgcolor: 'rgba(255,255,255,0.3)', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700 }}>
                    {activeCount}
                  </Box>
                )}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined" color="error" fullWidth
                onClick={clearAll}
                disabled={activeCount === 0}
                sx={{ borderRadius: 'var(--radius-md)', textTransform: 'none', fontFamily: 'var(--font-body)', fontWeight: 600 }}
              >
                Limpiar {activeCount > 0 && `(${activeCount})`}
              </Button>
            </Grid>
          </Grid>

          <Dialog open={modalOpen} fullWidth TransitionComponent={Transition} onClose={() => setModalOpen(false)}
            PaperProps={{ sx: { borderTopLeftRadius: 'var(--radius-xl)', borderTopRightRadius: 'var(--radius-xl)', pb: 1 } }}>
            <DialogTitle sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>Filtros</DialogTitle>
            <DialogContent>{FiltersContent}</DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3 }}>
              <Button color="error" onClick={() => { clearAll(); setModalOpen(false); }}
                sx={{ fontFamily: 'var(--font-body)', textTransform: 'none' }}>Limpiar</Button>
              <Button onClick={() => setModalOpen(false)} variant="contained"
                sx={{ fontFamily: 'var(--font-body)', textTransform: 'none', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent)', '&:hover': { backgroundColor: 'var(--accent-hover)' } }}>
                Aplicar
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Box sx={{ width: '100%', bgcolor: 'var(--bg-subtle)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', p: { xs: 2, md: 2.5 }, mx: { xs: 0, md: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: 'var(--accent)', fontSize: 20 }} />
              <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                Filtrar por categoría
              </Typography>
              {activeCount > 0 && (
                <Chip
                  label={`${activeCount} activo${activeCount > 1 ? 's' : ''}`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'var(--font-body)', fontWeight: 700, bgcolor: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent)' }}
                />
              )}
            </Box>
            <Button
              variant="outlined" color="error" size="small"
              onClick={clearAll}
              disabled={activeCount === 0}
              sx={{ borderRadius: 'var(--radius-md)', textTransform: 'none', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.78rem' }}
            >
              Limpiar {activeCount > 0 && `(${activeCount})`}
            </Button>
          </Box>
          {FiltersContent}
        </Box>
      )}
    </>
  );
};

export default CategoryFilter;